import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import {
  Container, Typography, Paper, Button, Stack, Box, Chip, Card, CardContent,
  IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
  Snackbar, Alert, Grid, Select, MenuItem, TextField, FormControl, InputLabel,
  Tabs, Tab, Divider, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow
} from '@mui/material';
import { MaterialReactTable } from 'material-react-table';
import { MRT_Localization_PT_BR } from 'material-react-table/locales/pt-BR';
import {
  Add, TrendingUp, TrendingDown, AccountBalance, FilterAlt, Close,
  Edit, Delete, Tune, Category
} from '@mui/icons-material';

const API_URL = process.env.REACT_APP_API_URL;

const formatCurrency = (value) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);

const formatDate = (date) => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('pt-BR');
};

const hoje = new Date().toISOString().split('T')[0];

const FORM_VAZIO = {
  tipo: 'Entrada',
  valor: '',
  categoria_id: '',
  descricao: '',
  data: hoje,
  forma_movimento: 'Dinheiro',
  observacao: ''
};

const formasMovimento = [
  'PIX', 'Dinheiro', 'Cartão de Crédito', 'Cartão de Débito',
  'Transferência Bancária (TED)', 'Transferência Bancária (DOC)',
  'Transferência', 'Cheque'
];

const inputStyle = {
  '& .MuiOutlinedInput-root': {
    '&:hover fieldset': { borderColor: '#f78b00' },
    '&.Mui-focused fieldset': { borderColor: '#f78b00', borderWidth: 2 }
  },
  '& .MuiInputLabel-root.Mui-focused': { color: '#f78b00' }
};

export default function Caixa() {
  const [movimentos, setMovimentos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [tabIndex, setTabIndex] = useState(2); // padrão: Todos

  const [dataInicio, setDataInicio] = useState(hoje);
  const [dataFim, setDataFim] = useState(hoje);

  // Lançamento / Editar
  const [openDialog, setOpenDialog] = useState(false);
  const [editando, setEditando] = useState(null);
  const [novaMov, setNovaMov] = useState(FORM_VAZIO);

  // Gerenciar Atributos
  const [atributosOpen, setAtributosOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [catEditando, setCatEditando] = useState(null);
  const [catForm, setCatForm] = useState({ nome: '', descricao: '' });

  // Quick create
  const [quickOpen, setQuickOpen] = useState(false);
  const [quickForm, setQuickForm] = useState({ nome: '', descricao: '' });

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const token = () => localStorage.getItem('token');
  const authHeader = () => ({ headers: { Authorization: `Bearer ${token()}` } });

  useEffect(() => {
    carregarMovimentos();
    carregarCategorias();
  }, []);

  useEffect(() => {
    carregarMovimentos();
  }, [dataInicio, dataFim]);

  // ========== CARREGAMENTOS ==========
  const carregarMovimentos = async () => {
    try {
      const { data } = await axios.get(
        `${API_URL}/caixa-movimento?dataInicio=${dataInicio}&dataFim=${dataFim}`,
        authHeader()
      );
      setMovimentos(data || []);
    } catch {
      setSnackbar({ open: true, message: 'Erro ao carregar movimentações do caixa', severity: 'error' });
    }
  };

  const carregarCategorias = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/categorias-pagamento`, authHeader());
      setCategorias(data || []);
    } catch {
      setCategorias([]);
    }
  };

  // ========== FILTROS RÁPIDOS ==========
  const aplicarFiltroRapido = (dias) => {
    const fim = new Date();
    const inicio = new Date();
    inicio.setDate(fim.getDate() - dias);
    setDataFim(fim.toISOString().split('T')[0]);
    setDataInicio(inicio.toISOString().split('T')[0]);
  };

  // ========== STATS ==========
  const movsFiltrados = useMemo(() => {
    if (tabIndex === 0) return movimentos.filter(m => m.tipo === 'Entrada');
    if (tabIndex === 1) return movimentos.filter(m => m.tipo === 'Saída');
    return movimentos;
  }, [movimentos, tabIndex]);

  const stats = useMemo(() => {
    const entradas = movimentos.filter(m => m.tipo === 'Entrada').reduce((s, m) => s + parseFloat(m.valor || 0), 0);
    const saidas = movimentos.filter(m => m.tipo === 'Saída').reduce((s, m) => s + parseFloat(m.valor || 0), 0);
    return { entradas, saidas, saldo: entradas - saidas };
  }, [movimentos]);

  // ========== LANÇAMENTO ==========
  const handleOpenDialog = (mov = null) => {
    if (mov) {
      setNovaMov({
        tipo: mov.tipo || 'Entrada',
        valor: mov.valor || '',
        categoria_id: mov.categoria_id || '',
        descricao: mov.descricao || '',
        data: mov.data?.split('T')[0] || hoje,
        forma_movimento: mov.forma_movimento || 'Dinheiro',
        observacao: mov.observacao || ''
      });
      setEditando(mov.id);
    } else {
      setNovaMov(FORM_VAZIO);
      setEditando(null);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditando(null);
    setNovaMov(FORM_VAZIO);
  };

  const handleChange = (e) => setNovaMov({ ...novaMov, [e.target.name]: e.target.value });

  const handleSalvar = async () => {
    if (!novaMov.valor || !novaMov.descricao || !novaMov.data) {
      setSnackbar({ open: true, message: 'Preencha os campos obrigatórios: valor, descrição e data', severity: 'warning' });
      return;
    }
    try {
      const dados = {
        ...novaMov,
        valor: parseFloat(novaMov.valor),
        origem: editando ? novaMov.origem : 'Lançamento Manual'
      };
      if (editando) {
        await axios.put(`${API_URL}/caixa-movimento/${editando}`, dados, authHeader());
        setSnackbar({ open: true, message: 'Lançamento atualizado com sucesso!', severity: 'success' });
      } else {
        await axios.post(`${API_URL}/caixa-movimento`, dados, authHeader());
        setSnackbar({ open: true, message: 'Lançamento registrado com sucesso!', severity: 'success' });
      }
      carregarMovimentos();
      handleCloseDialog();
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.mensagem || 'Erro ao salvar lançamento', severity: 'error' });
    }
  };

  const handleExcluir = async (id) => {
    if (!window.confirm('Deseja realmente excluir este lançamento?')) return;
    try {
      await axios.delete(`${API_URL}/caixa-movimento/${id}`, authHeader());
      setSnackbar({ open: true, message: 'Lançamento excluído!', severity: 'success' });
      carregarMovimentos();
    } catch {
      setSnackbar({ open: true, message: 'Erro ao excluir lançamento', severity: 'error' });
    }
  };

  // ========== GERENCIAR ATRIBUTOS — CATEGORIAS ==========
  const abrirCatDialog = (cat = null) => {
    if (cat) { setCatForm({ nome: cat.nome || '', descricao: cat.descricao || '' }); setCatEditando(cat.id); }
    else { setCatForm({ nome: '', descricao: '' }); setCatEditando(null); }
    setCatOpen(true);
  };

  const fecharCatDialog = () => { setCatOpen(false); setCatEditando(null); setCatForm({ nome: '', descricao: '' }); };

  const salvarCategoria = async () => {
    if (!catForm.nome.trim()) { setSnackbar({ open: true, message: 'Nome é obrigatório', severity: 'warning' }); return; }
    try {
      if (catEditando) {
        await axios.put(`${API_URL}/categorias-pagamento/${catEditando}`, catForm, authHeader());
        setSnackbar({ open: true, message: 'Categoria atualizada!', severity: 'success' });
      } else {
        await axios.post(`${API_URL}/categorias-pagamento`, catForm, authHeader());
        setSnackbar({ open: true, message: 'Categoria criada!', severity: 'success' });
      }
      fecharCatDialog();
      carregarCategorias();
    } catch { setSnackbar({ open: true, message: 'Erro ao salvar categoria', severity: 'error' }); }
  };

  const excluirCategoria = async (id) => {
    if (!window.confirm('Excluir esta categoria?')) return;
    try {
      await axios.delete(`${API_URL}/categorias-pagamento/${id}`, authHeader());
      setSnackbar({ open: true, message: 'Categoria excluída!', severity: 'success' });
      carregarCategorias();
    } catch { setSnackbar({ open: true, message: 'Erro ao excluir categoria', severity: 'error' }); }
  };

  // ========== QUICK CREATE ==========
  const salvarQuick = async () => {
    if (!quickForm.nome.trim()) { setSnackbar({ open: true, message: 'Nome é obrigatório', severity: 'warning' }); return; }
    try {
      const { data } = await axios.post(`${API_URL}/categorias-pagamento`, quickForm, authHeader());
      await carregarCategorias();
      setNovaMov(f => ({ ...f, categoria_id: data.id || f.categoria_id }));
      setSnackbar({ open: true, message: 'Categoria criada!', severity: 'success' });
      setQuickOpen(false);
    } catch { setSnackbar({ open: true, message: 'Erro ao criar categoria', severity: 'error' }); }
  };

  // ========== COLUNAS ==========
  const colunas = useMemo(() => [
    { accessorKey: 'id', header: 'ID', size: 60 },
    { accessorKey: 'data', header: 'Data', size: 100, Cell: ({ cell }) => formatDate(cell.getValue()) },
    {
      accessorKey: 'tipo', header: 'Tipo', size: 90,
      Cell: ({ cell }) => {
        const t = cell.getValue();
        return <Chip label={t} color={t === 'Entrada' ? 'success' : 'error'} size="small" variant="outlined" />;
      }
    },
    { accessorKey: 'categoria_nome', header: 'Categoria', size: 140, Cell: ({ cell }) => cell.getValue() || '-' },
    { accessorKey: 'descricao', header: 'Descrição', size: 220 },
    { accessorKey: 'forma_movimento', header: 'Forma', size: 130, Cell: ({ cell }) => cell.getValue() || '-' },
    { accessorKey: 'origem', header: 'Origem', size: 140, Cell: ({ cell }) => cell.getValue() || '-' },
    {
      accessorKey: 'valor', header: 'Valor', size: 110,
      Cell: ({ cell, row }) => (
        <Typography fontWeight={700}
          sx={{ color: row.original.tipo === 'Entrada' ? '#2e7d32' : '#c62828' }}>
          {formatCurrency(cell.getValue())}
        </Typography>
      )
    },
    {
      accessorKey: 'acoes', header: 'Ações', size: 90,
      Cell: ({ row }) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Editar">
            <IconButton size="small" color="primary" onClick={() => handleOpenDialog(row.original)}>
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Excluir">
            <IconButton size="small" color="error" onClick={() => handleExcluir(row.original.id)}>
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      )
    }
  ], []);

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* HEADER */}
      <Box sx={{
        background: 'linear-gradient(135deg, #1f1f1f 0%, #3a3a3a 100%)',
        p: 3, borderRadius: 2, mb: 3,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#fff'
      }}>
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ color: '#f78b00' }}>
            💰 Controle de Caixa
          </Typography>
          <Typography variant="body2" sx={{ color: '#aaa', mt: 0.5 }}>
            Movimentações financeiras do período
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          <Button variant="outlined" startIcon={<Tune />} onClick={() => setAtributosOpen(true)}
            sx={{ borderColor: '#f78b00', color: '#f78b00', fontWeight: 600, '&:hover': { bgcolor: 'rgba(247,139,0,0.1)' } }}>
            Gerenciar Atributos
          </Button>
          <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenDialog()}
            sx={{ bgcolor: '#f78b00', fontWeight: 700, '&:hover': { bgcolor: '#e67e00' } }}>
            Lançamento Avulso
          </Button>
        </Stack>
      </Box>

      {/* CARDS DE ESTATÍSTICAS */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ bgcolor: '#e8f5e9', borderTop: '4px solid #4caf50' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <TrendingUp sx={{ fontSize: 40, color: '#4caf50' }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">Total de Entradas</Typography>
                  <Typography variant="h5" fontWeight={700} sx={{ color: '#2e7d32' }}>
                    {formatCurrency(stats.entradas)}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ bgcolor: '#ffebee', borderTop: '4px solid #f44336' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <TrendingDown sx={{ fontSize: 40, color: '#f44336' }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">Total de Saídas</Typography>
                  <Typography variant="h5" fontWeight={700} sx={{ color: '#c62828' }}>
                    {formatCurrency(stats.saidas)}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ bgcolor: stats.saldo >= 0 ? '#e3f2fd' : '#fff3e0', borderTop: `4px solid ${stats.saldo >= 0 ? '#2196f3' : '#ff9800'}` }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <AccountBalance sx={{ fontSize: 40, color: stats.saldo >= 0 ? '#1565c0' : '#e65100' }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">Saldo do Período</Typography>
                  <Typography variant="h5" fontWeight={700} sx={{ color: stats.saldo >= 0 ? '#1565c0' : '#e65100' }}>
                    {formatCurrency(stats.saldo)}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* FILTROS */}
      <Paper sx={{ p: 3, mb: 3, bgcolor: '#f8f9fa', borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <FilterAlt sx={{ color: '#f78b00', fontSize: 22 }} />
          <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#1f1f1f' }}>
            FILTRAR POR PERÍODO
          </Typography>
        </Box>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={2}>
            <TextField fullWidth label="Data Início" type="date" value={dataInicio}
              onChange={(e) => setDataInicio(e.target.value)} InputLabelProps={{ shrink: true }} sx={inputStyle} />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField fullWidth label="Data Fim" type="date" value={dataFim}
              onChange={(e) => setDataFim(e.target.value)} InputLabelProps={{ shrink: true }} sx={inputStyle} />
          </Grid>
          <Grid item xs={12} md={8} sx={{ display: 'flex', gap: 1, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <Button variant="outlined" size="small" onClick={() => aplicarFiltroRapido(7)}
              sx={{ borderColor: '#f78b00', color: '#f78b00', fontWeight: 'bold' }}>
              Últimos 7 dias
            </Button>
            <Button variant="outlined" size="small" onClick={() => aplicarFiltroRapido(30)}
              sx={{ borderColor: '#f78b00', color: '#f78b00', fontWeight: 'bold' }}>
              Últimos 30 dias
            </Button>
            <Button variant="outlined" size="small" onClick={() => { setDataInicio(hoje); setDataFim(hoje); }}
              startIcon={<Close />} sx={{ borderColor: '#999', color: '#999', fontWeight: 'bold' }}>
              Hoje
            </Button>
          </Grid>
        </Grid>
        <Typography variant="caption" sx={{ color: '#666', fontWeight: 500 }}>
          Período: <strong>{formatDate(dataInicio)}</strong> a <strong>{formatDate(dataFim)}</strong>
          {' — '}{movimentos.length} movimento(s)
        </Typography>
      </Paper>

      {/* TABELA COM TABS */}
      <Paper sx={{ p: 2, borderRadius: 2 }}>
        <Tabs value={tabIndex} onChange={(_, v) => setTabIndex(v)}
          sx={{ mb: 2, borderBottom: '1px solid #e0e0e0', '& .Mui-selected': { color: '#f78b00' }, '& .MuiTabs-indicator': { bgcolor: '#f78b00' } }}>
          <Tab label={`Entradas (${movimentos.filter(m => m.tipo === 'Entrada').length})`} />
          <Tab label={`Saídas (${movimentos.filter(m => m.tipo === 'Saída').length})`} />
          <Tab label={`Todos (${movimentos.length})`} />
        </Tabs>
        <MaterialReactTable
          columns={colunas} data={movsFiltrados}
          localization={MRT_Localization_PT_BR}
          enablePagination enableSorting enableColumnFilters enableDensityToggle
          initialState={{ density: 'compact', pagination: { pageSize: 25 } }}
          muiTableProps={{ sx: { tableLayout: 'fixed' } }}
        />
      </Paper>

      {/* ========== MODAL: LANÇAMENTO AVULSO / EDITAR ========== */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden' } }}>
        <DialogTitle sx={{
          background: 'linear-gradient(135deg, #1f1f1f 0%, #3a3a3a 100%)',
          color: '#f78b00', fontWeight: 700,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 2.5
        }}>
          💰 {editando ? 'Editar Lançamento' : 'Novo Lançamento Avulso'}
          <IconButton onClick={handleCloseDialog} sx={{ color: '#fff' }} size="small"><Close /></IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <Grid container spacing={2.5} sx={{ mt: 0 }}>

            {/* IDENTIFICAÇÃO */}
            <Grid item xs={12}>
              <Typography variant="caption" sx={{ color: '#f78b00', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>
                Identificação
              </Typography>
              <Divider sx={{ mt: 0.5, mb: 1.5, borderColor: '#f78b00', opacity: 0.3 }} />
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth sx={inputStyle}>
                <InputLabel>Tipo *</InputLabel>
                <Select name="tipo" value={novaMov.tipo} onChange={handleChange} label="Tipo *">
                  <MenuItem value="Entrada">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TrendingUp sx={{ color: '#2e7d32', fontSize: 18 }} /> Entrada
                    </Box>
                  </MenuItem>
                  <MenuItem value="Saída">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TrendingDown sx={{ color: '#c62828', fontSize: 18 }} /> Saída
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField name="valor" label="Valor (R$) *" type="number" value={novaMov.valor}
                onChange={handleChange} fullWidth inputProps={{ step: '0.01', min: '0' }} sx={inputStyle} />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField name="data" label="Data *" type="date" value={novaMov.data}
                onChange={handleChange} fullWidth InputLabelProps={{ shrink: true }} sx={inputStyle} />
            </Grid>

            <Grid item xs={12}>
              <TextField name="descricao" label="Descrição *" value={novaMov.descricao}
                onChange={handleChange} fullWidth
                placeholder="Ex: Recebimento de aluguel, pagamento de fornecedor..."
                sx={inputStyle}
              />
            </Grid>

            {/* CLASSIFICAÇÃO */}
            <Grid item xs={12}>
              <Typography variant="caption" sx={{ color: '#f78b00', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>
                Classificação
              </Typography>
              <Divider sx={{ mt: 0.5, mb: 1.5, borderColor: '#f78b00', opacity: 0.3 }} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <FormControl fullWidth sx={inputStyle}>
                  <InputLabel>Categoria</InputLabel>
                  <Select name="categoria_id" value={novaMov.categoria_id} onChange={handleChange} label="Categoria">
                    <MenuItem value=""><em>Nenhuma</em></MenuItem>
                    {categorias.map(cat => <MenuItem key={cat.id} value={cat.id}>{cat.nome}</MenuItem>)}
                  </Select>
                  <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, ml: 1.5 }}>
                    {`${API_URL}/categorias-pagamento — ${categorias.length} cadastrada(s)`}
                  </Typography>
                </FormControl>
                <Tooltip title="Criar nova categoria">
                  <IconButton onClick={() => { setQuickForm({ nome: '', descricao: '' }); setQuickOpen(true); }}
                    sx={{ mt: 1, color: '#f78b00', border: '1px solid #f78b00', borderRadius: 1, '&:hover': { bgcolor: 'rgba(247,139,0,0.1)' } }}
                    size="small">
                    <Add fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth sx={inputStyle}>
                <InputLabel>Forma</InputLabel>
                <Select name="forma_movimento" value={novaMov.forma_movimento} onChange={handleChange} label="Forma">
                  {formasMovimento.map(f => <MenuItem key={f} value={f}>{f}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>

            {/* OBSERVAÇÃO */}
            <Grid item xs={12}>
              <Typography variant="caption" sx={{ color: '#f78b00', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>
                Observação
              </Typography>
              <Divider sx={{ mt: 0.5, mb: 1.5, borderColor: '#f78b00', opacity: 0.3 }} />
            </Grid>

            <Grid item xs={12}>
              <TextField name="observacao" label="Observação" value={novaMov.observacao}
                onChange={handleChange} fullWidth multiline rows={3}
                placeholder="Informações adicionais, referências, detalhes do lançamento..."
                sx={inputStyle}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #eee', gap: 1 }}>
          <Button onClick={handleCloseDialog} variant="outlined" sx={{ borderColor: '#ccc', color: '#666' }}>Cancelar</Button>
          <Button onClick={handleSalvar} variant="contained"
            sx={{ bgcolor: '#f78b00', fontWeight: 700, px: 4, '&:hover': { bgcolor: '#e67e00' } }}>
            {editando ? 'Atualizar' : 'Registrar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ========== MODAL: GERENCIAR ATRIBUTOS ========== */}
      <Dialog open={atributosOpen} onClose={() => setAtributosOpen(false)} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden', minHeight: 480 } }}>
        <DialogTitle sx={{
          background: 'linear-gradient(135deg, #1f1f1f 0%, #3a3a3a 100%)',
          color: '#f78b00', fontWeight: 700,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 2.5
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tune sx={{ color: '#f78b00' }} /> Gerenciar Atributos Financeiros
          </Box>
          <IconButton onClick={() => setAtributosOpen(false)} sx={{ color: '#fff' }} size="small"><Close /></IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box>
              <Typography variant="subtitle1" fontWeight={700}>
                <Category fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle', color: '#f78b00' }} />
                Categorias de Pagamento
              </Typography>
              <Typography variant="caption" color="text.secondary">Endpoint: {API_URL}/categorias-pagamento</Typography>
            </Box>
            <Button variant="contained" startIcon={<Add />} size="small" onClick={() => abrirCatDialog()}
              sx={{ bgcolor: '#f78b00', '&:hover': { bgcolor: '#e67e00' } }}>
              Nova Categoria
            </Button>
          </Box>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 700 }}>ID</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Nome</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Descrição</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {categorias.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ color: '#999', py: 3 }}>Nenhuma categoria cadastrada</TableCell>
                  </TableRow>
                ) : categorias.map(cat => (
                  <TableRow key={cat.id} hover>
                    <TableCell>{cat.id}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{cat.nome}</TableCell>
                    <TableCell sx={{ color: '#666' }}>{cat.descricao || '-'}</TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Tooltip title="Editar">
                          <IconButton size="small" color="primary" onClick={() => abrirCatDialog(cat)}><Edit fontSize="small" /></IconButton>
                        </Tooltip>
                        <Tooltip title="Excluir">
                          <IconButton size="small" color="error" onClick={() => excluirCategoria(cat.id)}><Delete fontSize="small" /></IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
      </Dialog>

      {/* ========== DIALOG: EDITAR/CRIAR CATEGORIA ========== */}
      <Dialog open={catOpen} onClose={fecharCatDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{catEditando ? 'Editar Categoria' : 'Nova Categoria'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1.5 }}>
            <TextField label="Nome *" value={catForm.nome} onChange={e => setCatForm(f => ({ ...f, nome: e.target.value }))}
              fullWidth autoFocus placeholder="Ex: Aluguel, Energia, Combustível..." sx={inputStyle} />
            <TextField label="Descrição" value={catForm.descricao} onChange={e => setCatForm(f => ({ ...f, descricao: e.target.value }))}
              fullWidth multiline rows={3} placeholder="Descrição detalhada..." sx={inputStyle} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={fecharCatDialog} variant="outlined" sx={{ borderColor: '#ccc', color: '#666' }}>Cancelar</Button>
          <Button onClick={salvarCategoria} variant="contained" sx={{ bgcolor: '#f78b00', '&:hover': { bgcolor: '#e67e00' } }}>
            {catEditando ? 'Atualizar' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ========== DIALOG: QUICK CREATE ========== */}
      <Dialog open={quickOpen} onClose={() => setQuickOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Nova Categoria
          <IconButton onClick={() => setQuickOpen(false)} size="small"><Close fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Nome *" value={quickForm.nome} onChange={e => setQuickForm(f => ({ ...f, nome: e.target.value }))}
              fullWidth autoFocus placeholder="Ex: Aluguel, Energia..." sx={inputStyle} />
            <TextField label="Descrição" value={quickForm.descricao} onChange={e => setQuickForm(f => ({ ...f, descricao: e.target.value }))}
              fullWidth placeholder="Opcional" sx={inputStyle} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setQuickOpen(false)} variant="outlined" sx={{ borderColor: '#ccc', color: '#666' }}>Cancelar</Button>
          <Button onClick={salvarQuick} variant="contained" sx={{ bgcolor: '#f78b00', '&:hover': { bgcolor: '#e67e00' } }}>
            Criar e Selecionar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snackbar.severity} variant="filled">{snackbar.message}</Alert>
      </Snackbar>
    </Container>
  );
}
